"use client";

import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import { currentIndiaDate, formatVedicEngineDate } from "@/lib/vedic-engine-date";

export interface EngineDateItem {
  id: string;
  name_en: string;
  name_hi?: string | null;
  date: string;
  month_hi?: string | null;
  /** The day the fast is kept when it is not `date` (Purnima Vrat on Chaturdashi). */
  fasting_date?: string | null;
}

export interface EngineDateYear<T extends EngineDateItem> {
  year: number;
  entries: T[];
}

interface EngineDateTableProps<T extends EngineDateItem> {
  years: EngineDateYear<T>[];
  tabLabel: string;
  yearLabel?: string;
  itemHeader: string;
  dateHeader?: string;
  detailHeader: string;
  footer: ReactNode;
  detail?: (item: T) => ReactNode;
  itemName?: (item: T) => ReactNode;
  dateLocale?: "en" | "hi";
  nextLabel?: ReactNode;
}

function nextEngineDate<T extends EngineDateItem>(
  years: EngineDateYear<T>[],
  today: string
): string | null {
  const upcoming = years
    .flatMap((year) => year.entries.map((entry) => entry.date))
    .filter((date) => date >= today)
    .sort();
  return upcoming[0] ?? null;
}

/**
 * The shared engine date table. It has no empty or error state on purpose: the
 * page loaders (`loadVratDates` in src/lib/vrat-dates.ts) throw before render
 * when the engine cannot supply a year's dates, so a vrat page never ships a
 * table-shaped placeholder or "try again later" copy.
 */
export default function EngineDateTable<T extends EngineDateItem>({
  years,
  tabLabel,
  yearLabel = "year",
  itemHeader,
  dateHeader = "Date",
  detailHeader,
  footer,
  detail = (item) => item.month_hi || "—",
  itemName = (item) => item.name_en,
  dateLocale = "en",
  nextLabel = "Next",
}: EngineDateTableProps<T>) {
  const today = currentIndiaDate();
  const available = years.filter((year) => year.entries.length > 0);
  const fallbackYear = available[0]?.year ?? years[0]?.year ?? Number(today.slice(0, 4));
  const [selected, setSelected] = useState(fallbackYear);
  const activeYear = years.some((year) => year.year === selected) ? selected : fallbackYear;
  const active = years.find((year) => year.year === activeYear);
  const nextDate = useMemo(() => nextEngineDate(years, today), [years, today]);

  return (
    <div>
      <div
        className="mb-5 flex flex-wrap gap-2"
        role="tablist"
        aria-label={`${tabLabel} ${yearLabel}`}
      >
        {years.map((year) => {
          const isActive = year.year === activeYear;
          return (
            <button
              key={year.year}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={year.entries.length === 0}
              onClick={() => setSelected(year.year)}
              className={`rounded-lg border border-border-interactive px-4 py-1.5 text-body-small font-semibold transition disabled:opacity-40 ${
                isActive
                  ? "bg-surface-brand-strong text-text-on-brand"
                  : "bg-surface-brand-subtle text-intent-brand"
              }`}
            >
              {tabLabel} {year.year}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border-default">
        <table className="w-full border-collapse text-left text-body-small">
          <thead>
            <tr className="bg-surface-brand-subtle">
              {[itemHeader, dateHeader, detailHeader].map((header) => (
                <th key={header} className="px-3 py-2.5 font-semibold text-text-primary">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(active?.entries ?? []).map((item) => {
              const isNext = item.date === nextDate;
              return (
                <tr
                  key={item.id}
                  // Machine-readable "Next" claim: the live correctness check
                  // (execution/check_live_page_correctness.py) fails a page whose
                  // marked date is already behind today's IST date.
                  data-ishvaram-next-date={isNext ? item.date : undefined}
                  className={`border-t border-border-subtle ${
                    isNext ? "bg-surface-brand-subtle" : ""
                  }`}
                >
                  <td className="px-3 py-2.5">
                    <span className="font-semibold text-text-primary">{itemName(item)}</span>
                    {dateLocale === "en" && item.name_hi ? (
                      <span className="ml-1.5 text-text-muted">({item.name_hi})</span>
                    ) : null}
                    {isNext ? (
                      <span className="ml-2 inline-block rounded bg-surface-brand-strong px-1.5 py-0.5 text-caption font-bold uppercase tracking-wide text-text-on-brand">
                        {nextLabel}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-text-primary">
                    {formatVedicEngineDate(item.date, dateLocale)}
                    {item.fasting_date && item.fasting_date !== item.date ? (
                      <span className="block text-caption text-text-muted">
                        {dateLocale === "hi" ? "व्रत: " : "Fast on "}
                        {formatVedicEngineDate(item.fasting_date, dateLocale)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-text-muted">{detail(item)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-caption text-text-muted">{footer}</p>
    </div>
  );
}
